/**
 * Task templates, example prompts, and categories for the task creation flow.
 * Templates use IntegrationRef for logo display instead of Lucide icons.
 */
import { INTEGRATIONS, type IntegrationRef } from "./integration-logos";

/**
 * Example prompts for task creation placeholder rotation.
 * High-value, cross-tool, actionable business operator use cases.
 */
export const EXAMPLE_PROMPTS = [
	// Google Ads & Paid
	"Pull my Google Ads monthly report — compare CPA, CTR, and conversions vs last month. If anything changed more than 15%, dig into why",
	"Check Google Ads search terms from the last 7 days and add anything irrelevant as negative keywords",
	"Write 3 new headline variations for my best-performing Google Ads campaign to A/B test this week",
	"Check Meta Ads performance and pause any ad sets with CPA over $50",
	// Outreach & Prospecting
	"Find 20 SaaS founders on LinkedIn who match our ICP and draft personalized connection messages for each",
	"Search for YouTube influencers in our niche with 10k-100k subscribers and build a lead list in Sheets with their email and follower count",
	"Find 10 pre-seed angel investors who invest in our space, research their portfolio, and draft personalized intro emails",
	"Draft a 3-email cold outreach sequence for CTOs at Series A startups — personalize the opener based on their company",
	// Content & Growth
	"Review our latest GitHub deployments and Linear tickets from this month, then write a product update blog post",
	"Make a content plan for next month — 4 newsletter topics and 4 social posts. Check Slack and GitHub for what's new",
	"Review our top 5 blog posts by traffic, then write a new technical blog post on a related topic with code examples",
	"Research trending topics in our industry, draft 5 blog post outlines with target keywords, and save them to Notion",
	// CRO & Analytics
	"Review Google Analytics and PostHog data for our signup page. Where are visitors dropping off and what should we test?",
	"Pull Google Ads KPIs into the tracking Sheet and flag any metric that moved more than 10% week-over-week",
	"Look at our PostHog signup funnel, find the biggest drop-off, and write up 3 experiments to test in Notion",
	// Email Marketing
	"Review our drip email sequence — which emails have the best and worst CTR? Suggest subject line and copy changes for the bottom 3",
	"Analyze our Klaviyo welcome flow performance and draft 2 new email variations to A/B test",
	// Sales & CRM
	"Check HubSpot for deals with no activity in 2 weeks, draft follow-up emails in Gmail, and log the outreach in Sheets",
	"How many leads came in from HubSpot this month and which source performed best?",
	"What's our current MRR, churn rate, and net revenue retention in Stripe?",
	// Recurring Ops
	"Every Monday, pull Stripe MRR and churn, update the Google Sheets dashboard, and alert #finance in Slack if churn is up",
	"Every morning, check Google Ads spend vs budget and flag any campaign pacing more than 20% over daily budget",
	"Every Friday, compile key discussions from Slack and post a weekly recap in Notion",
	// Finance & Admin
	"Check Xero for invoices overdue by more than 14 days and draft a polite payment reminder email for each",
	"Review this month's expenses in QuickBooks, categorize untagged ones, and flag anything unusual",
	"Pull Stripe subscription data and calculate net revenue retention, expansion revenue, and contraction by plan tier",
	// Multi-tool
	"Review today's Sentry errors, create Linear tickets for critical ones, and post a summary to #engineering in Slack",
	"Scan our Jira backlog for tickets older than 3 months and recommend which to close, merge, or reprioritize",
	"Audit Slack for dead channels with zero messages in 30 days and recommend which to archive",
	"Research our top 3 competitors' pricing and features, then create a comparison table in Notion",
	// Finance & Revenue
	"Pull Stripe failed payments from the last 7 days, draft recovery emails for each, and calculate total MRR at risk",
	"Build a P&L report from Xero — revenue by stream, expenses by category, margins — and compare to last month",
	"Calculate our cash runway based on QuickBooks burn rate and project when we'll need to fundraise",
	"Which Shopify products are actually profitable after returns and shipping? Build a product P&L in Sheets",
	// Sales Pipeline
	"Check Pipedrive for how long deals sit in each stage — where's our pipeline bottleneck?",
	"Score every open deal in our CRM by win likelihood based on activity, age, and engagement signals",
	"Pull DocuSign pending contracts, calculate total revenue at risk, and send follow-ups for the biggest deals",
	"Analyze our cold email sequences — which ones get replies and which get ignored? Recommend improvements",
	// Marketing Intelligence
	"Compare our Ahrefs backlink profile to competitors and find 10 sites that link to them but not us",
	"Check Semrush keyword rankings vs last week — which pages are gaining and which are losing ground?",
	"Audit our Mailchimp list for inactive subscribers costing us money and recommend a cleanup plan",
	"Pull Search Console data and find pages with declining traffic that need content refreshes",
	// Community & Growth
	"Find Reddit threads where people are asking questions we can help with and draft helpful responses — no spam, just genuine value",
	"Check which new Discord members joined this week but haven't posted yet — draft welcome DMs to get them engaged",
	// Competitive Intelligence
	"Research what Google and Facebook ads our competitors are running, compare to our current campaigns, and draft a proposal for what to change",
	// Finance & Budgeting
	"Pull QuickBooks actuals for this month, compare every line item to our budget in Sheets, and highlight where we're overspending",
	"Review all our vendor subscriptions and recurring charges — which services aren't getting used? Where can we cut costs?",
	"Pull this month's key metrics from Stripe and GA, save them to our monthly reporting Sheet, and give me the highlights, lowlights, and any red flags",
	"Compare our revenue forecast in the finance model with Stripe actuals — where were we off and how should we improve the forecasting sheet?",
	// Customer Growth
	"Find churned Stripe customers from last month who were previously active — draft personalized win-back emails for the ones most likely to return",
	"Which customers are close to hitting their plan limits? Find upsell candidates in Stripe and draft upgrade nudge emails",
	// SEO Content
	"Find low-competition keywords we can rank for using Keyword Planner and Search Console, then draft blog posts targeting the best opportunities",
];

/**
 * Task template categories
 */
export const CATEGORIES = [
	"All",
	"Marketing",
	"Sales",
	"Product",
	"Operations",
	"Finance",
] as const;

export type TaskCategory = (typeof CATEGORIES)[number];

/**
 * Task template definition.
 * `integrations[0]` is the primary logo shown on the card.
 */
export interface TaskTemplate {
	id: string;
	title: string;
	description: string;
	prompt: string;
	category: Exclude<TaskCategory, "All">;
	integrations: IntegrationRef[];
}

const I = INTEGRATIONS;

/**
 * Pre-built task templates for quick creation.
 * Covers major integrations across all categories.
 */
export const TEMPLATES: TaskTemplate[] = [
	// ── Marketing ──
	{
		id: "google-ads-negative-keywords",
		title: "Cut Wasted Ad Spend",
		description: "Analyze search terms and add negative keywords for wasted spend",
		prompt:
			"Analyze my Google Ads search terms report, identify irrelevant queries with spend over $10, and add them as negative keywords",
		category: "Marketing",
		integrations: [I["google-ads"]],
	},
	{
		id: "google-ads-monthly-report",
		title: "Monthly Google Ads Deep Dive",
		description:
			"Compare this month vs last — CPA, CTR, CVR, CPC — and dig into root causes for any big swings",
		prompt:
			"Pull our Google Ads performance for this month and compare to last month. For each key metric (CPA, CTR, CVR, CPC, spend, conversions), show the month-over-month change. If any metric moved more than 15%, dig deeper — which campaigns, ad groups, or keywords drove the change? Summarize findings and recommendations in Google Sheets and post the report to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I["google-sheets"], I.slack],
	},
	{
		id: "google-ads-weekly-report",
		title: "Weekly Google Ads Performance Check",
		description:
			"Weekly spend, conversions, CPA by campaign — flag anything off track and suggest adjustments",
		prompt:
			"Pull this week's Google Ads data broken down by campaign. For each, show spend, conversions, CPA, and CTR. Compare to last week and flag any campaign where CPA increased more than 20% or CTR dropped. Suggest specific adjustments — pause keywords, adjust bids, update copy. Update the weekly tracker in Google Sheets and post a summary to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I["google-sheets"], I.slack],
	},
	{
		id: "google-ads-daily-check",
		title: "Morning Google Ads Check-In",
		description:
			"Quick daily check — are campaigns spending on budget? Any anomalies in cost or performance?",
		prompt:
			"Check yesterday's Google Ads performance: total spend vs daily budget, conversions, CPA, and CTR by campaign. Flag any campaign that overspent by more than 20% of daily budget or where CPA spiked vs the 7-day average. Post a quick morning summary to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I.slack],
	},
	{
		id: "google-ads-ab-test-copy",
		title: "Write A/B Test Ad Copy Variations",
		description:
			"Analyze top-performing ads, then write new headline and description variations to test for better CTR",
		prompt:
			"Review our Google Ads campaigns and identify the 3 ads with the highest CTR and the 3 with the lowest. Analyze what's different — headlines, descriptions, CTAs. Then write 3 new headline + description variations for each underperformer, using patterns from the winners. Save the test plan with old vs new copy side-by-side in Google Sheets",
		category: "Marketing",
		integrations: [I["google-ads"], I["google-sheets"]],
	},
	{
		id: "google-ads-account-audit",
		title: "Full Google Ads Account Review",
		description:
			"Audit account structure, quality scores, wasted spend, ad extensions, and bidding — recommend improvements",
		prompt:
			"Do a full audit of our Google Ads account. Check: (1) campaign structure — are campaigns well-organized? (2) quality scores — which keywords are below 5? (3) wasted spend — search terms burning budget, (4) ad extensions — are we using all available? (5) bidding strategy — is it optimal? For each area, give a specific recommendation with expected impact. Save the full audit to Notion and post key action items to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I.notion, I.slack],
	},
	{
		id: "google-ads-landing-page-review",
		title: "Which Landing Pages Need Better Copy?",
		description:
			"Cross-reference ad clicks with landing page conversion rates — find pages where traffic isn't converting",
		prompt:
			"Pull Google Ads data by landing page URL. Identify pages with high click volume but low conversion rate — these are getting traffic but not converting. For each underperforming page, review the current headline and CTA vs the ad that sends traffic to it. Suggest specific copy changes to improve alignment. Save the analysis to Notion",
		category: "Marketing",
		integrations: [I["google-ads"], I["google-analytics"], I.notion],
	},
	{
		id: "meta-ads-monthly-report",
		title: "Monthly Meta Ads Performance Review",
		description:
			"Compare this month vs last — ROAS, CPA, CPM, frequency — and recommend creative and audience changes",
		prompt:
			"Pull our Meta Ads performance for this month and compare to last month. Break down by campaign: ROAS, CPA, CPM, frequency, and amount spent. Flag any campaigns where frequency is above 3 (ad fatigue) or CPA increased more than 20%. Recommend specific changes — new creatives, audience adjustments, or budget shifts. Update the tracker in Google Sheets and post to #marketing in Slack",
		category: "Marketing",
		integrations: [I["meta-ads"], I["google-sheets"], I.slack],
	},
	{
		id: "meta-ads-performance",
		title: "Pause Underperforming Ads",
		description: "Review Meta Ads and pause underperforming ad sets",
		prompt:
			"Check Meta Ads performance for the last 7 days, flag any ad sets with CPA above $50, and pause them. Post a summary to Slack",
		category: "Marketing",
		integrations: [I["meta-ads"], I.slack],
	},
	{
		id: "search-console-monitor",
		title: "Track Indexing & Ranking Drops",
		description: "Track indexing issues and ranking drops in Google Search Console",
		prompt:
			"Check Google Search Console for pages with significant ranking drops or indexing errors this week and summarize findings",
		category: "Marketing",
		integrations: [I["google-search-console"]],
	},
	{
		id: "semrush-content-gaps",
		title: "Find Content Opportunities",
		description: "Find ranking opportunities and create content briefs",
		prompt:
			"Review Semrush for keywords where competitors rank but we don't, then create content briefs in Google Docs for the top 3 opportunities",
		category: "Marketing",
		integrations: [I.semrush, I["google-docs"]],
	},
	{
		id: "mailchimp-campaign-report",
		title: "Campaign Performance Report",
		description: "Summarize Mailchimp campaign performance into a spreadsheet",
		prompt:
			"Pull the last 5 Mailchimp campaign results (open rate, click rate, unsubscribes) and update our Google Sheets tracking report",
		category: "Marketing",
		integrations: [I.mailchimp, I["google-sheets"]],
	},
	{
		id: "google-analytics-digest",
		title: "Daily Traffic Digest",
		description: "Post daily traffic highlights to Slack",
		prompt:
			"Every morning, pull yesterday's Google Analytics traffic summary (sessions, top pages, traffic sources) and post a digest to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-analytics"], I.slack],
	},
	{
		id: "linkedin-content-ideas",
		title: "Draft LinkedIn Posts",
		description: "Research trending topics and draft LinkedIn post ideas",
		prompt:
			"Research trending topics in our industry on LinkedIn and draft 5 post ideas with hooks and key talking points",
		category: "Marketing",
		integrations: [I.linkedin],
	},
	{
		id: "competitor-watch",
		title: "Competitor Watch",
		description: "Track competitor pricing and features, alert on changes",
		prompt:
			"Monitor our top 3 competitors' pricing pages and feature lists daily, alert me on Slack if anything changes",
		category: "Marketing",
		integrations: [I.slack],
	},
	{
		id: "twitter-social-listening",
		title: "Brand Mention Monitor",
		description: "Monitor brand mentions and sentiment on Twitter",
		prompt:
			"Check Twitter for mentions of our brand and competitors this week, summarize the sentiment, and flag anything that needs a response",
		category: "Marketing",
		integrations: [I.twitter],
	},
	{
		id: "youtube-channel-analytics",
		title: "What YouTube Content Should We Make More Of?",
		description:
			"Analyze top-performing videos by watch time and engagement, then draft a content strategy with 5 new video ideas",
		prompt:
			"Analyze our YouTube channel — which video topics get the best watch time and engagement? Draft 5 new video ideas based on what's working and save the content plan to Notion. Post the top insights to #marketing in Slack",
		category: "Marketing",
		integrations: [I.youtube, I.notion, I.slack],
	},
	{
		id: "ahrefs-backlink-monitor",
		title: "Spot Spammy Backlinks",
		description: "Find new backlinks and flag spammy domains",
		prompt:
			"Find new backlinks to our site in Ahrefs from the last 7 days, flag any from spammy or low-authority domains, and post the report to Slack",
		category: "Marketing",
		integrations: [I.ahrefs, I.slack],
	},
	{
		id: "competitor-research",
		title: "How Do We Stack Up Against Competitors?",
		description:
			"Research competitor pricing, features, and positioning, then build a comparison table in Notion",
		prompt:
			"Research our top 3 competitors using Tavily — compare their pricing, features, and positioning against ours. Create a comparison table in Notion and post the key takeaways to #marketing in Slack",
		category: "Marketing",
		integrations: [I.tavily, I.notion, I.slack],
	},
	{
		id: "content-calendar",
		title: "Plan Next Month's Blog Content",
		description:
			"Research trending topics and SEO gaps, then draft a content calendar with titles, keywords, and publish dates",
		prompt:
			"Use Google Analytics to find our top-performing blog content, research trending topics in our industry with Tavily, and draft a blog content calendar for next month in Notion — include title ideas, target keywords, and publish dates",
		category: "Marketing",
		integrations: [I["google-analytics"], I.tavily, I.notion],
	},
	{
		id: "discord-community-digest",
		title: "What's the Community Asking For?",
		description:
			"Extract feature requests and pain points from Discord, create tickets for recurring themes, and alert the team",
		prompt:
			"Review this week's Discord community discussions, extract feature requests and common pain points. Create Linear tickets for any request mentioned 3+ times and post a summary of community sentiment to #product in Slack",
		category: "Marketing",
		integrations: [I.discord, I.linear, I.slack],
	},
	{
		id: "klaviyo-email-performance",
		title: "Flag Low-Performing Emails",
		description: "Review email campaign metrics and flag underperformers",
		prompt:
			"Pull last week's Klaviyo email campaign metrics and flag any with open rates below 15%",
		category: "Marketing",
		integrations: [I.klaviyo],
	},
	{
		id: "brevo-campaign-summary",
		title: "Which Emails Are Flopping?",
		description:
			"Identify underperforming email campaigns, diagnose why, and recommend subject line and timing improvements",
		prompt:
			"Pull our Brevo email campaign data from this month. Identify campaigns with below-average open or click rates, diagnose likely causes (subject line, send time, audience), and draft specific recommendations. Post the analysis to #marketing in Slack",
		category: "Marketing",
		integrations: [I.brevo, I.slack],
	},
	{
		id: "sendgrid-delivery-report",
		title: "Fix Email Delivery Issues",
		description: "Check for email delivery issues and high bounce rates",
		prompt:
			"Check SendGrid for email delivery issues this week and flag any domains with high bounce rates",
		category: "Marketing",
		integrations: [I.sendgrid],
	},
	{
		id: "facebook-page-insights",
		title: "What's Working on Facebook?",
		description:
			"Analyze top-performing Facebook posts and draft new content based on what resonates",
		prompt:
			"Pull our Facebook page stats for this week — reach, engagement, and top posts. Analyze what content types and topics performed best, then draft 3 new post ideas based on the winners. Save the analysis and drafts to Notion",
		category: "Marketing",
		integrations: [I.facebook, I.notion],
	},
	{
		id: "reddit-brand-mentions",
		title: "What Are People Saying on Reddit?",
		description:
			"Find brand mentions, summarize sentiment, draft responses for negative threads, and log insights",
		prompt:
			"Search Reddit for mentions of our brand and product this week. Summarize the overall sentiment and key themes. Draft helpful responses for any negative or confused threads. Save the findings to Notion and alert #marketing in Slack if anything urgent comes up",
		category: "Marketing",
		integrations: [I.reddit, I.notion, I.slack],
	},
	{
		id: "eventbrite-event-summary",
		title: "Which Events Need More Signups?",
		description:
			"Check registration pacing for upcoming events, draft promo emails for underperforming ones",
		prompt:
			"Review registration numbers for our upcoming Eventbrite events. Flag any events below 50% capacity with less than 2 weeks to go. Draft promotional emails in Gmail for underperforming events and alert #marketing in Slack",
		category: "Marketing",
		integrations: [I.eventbrite, I.gmail, I.slack],
	},
	{
		id: "webflow-site-changes",
		title: "Catch Unpublished CMS Items",
		description: "Review CMS collections for unpublished or draft items",
		prompt:
			"Review our Webflow CMS collections and flag any items that are unpublished, in draft, or missing required fields",
		category: "Marketing",
		integrations: [I.webflow],
	},
	{
		id: "canva-design-review",
		title: "Are Our Designs On-Brand?",
		description:
			"Audit recent team designs for brand consistency — colors, fonts, logos — and flag anything off",
		prompt:
			"Review all Canva designs created by the team this week. Check each for brand consistency — correct logo usage, brand colors, approved fonts. Flag any that are off-brand with specific notes on what needs fixing and post the audit to #design in Slack",
		category: "Marketing",
		integrations: [I.canva, I.slack],
	},
	{
		id: "google-ads-budget-optimizer",
		title: "Move Ad Budget to What's Working",
		description:
			"Analyze campaign performance and reallocate budget from underperformers to top converters",
		prompt:
			"Analyze our Google Ads campaigns from the last 14 days. Identify the top 3 campaigns by conversion rate and the bottom 3 by CPA. Recommend specific budget reallocations — how much to shift from underperformers to winners. Post the recommendations to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I.slack],
	},
	{
		id: "klaviyo-revenue-attribution",
		title: "Which Email Flows Make Us Money?",
		description: "Rank email flows by attributed revenue and recommend which to optimize or sunset",
		prompt:
			"Pull revenue attribution data from our Klaviyo email flows. Rank flows by revenue generated, identify any with declining performance, and recommend which flows to optimize, A/B test, or sunset. Post the analysis to #marketing in Slack",
		category: "Marketing",
		integrations: [I.klaviyo, I.slack],
	},
	{
		id: "linkedin-growth-audit",
		title: "How Do We Grow on LinkedIn Faster?",
		description:
			"Analyze posting patterns, engagement trends, and audience growth to recommend a LinkedIn growth strategy",
		prompt:
			"Review our LinkedIn company page — posting frequency, engagement rates by content type, follower growth trends, and best-performing posts. Draft a growth strategy with specific recommendations on what to post more of and save it to Notion",
		category: "Marketing",
		integrations: [I.linkedin, I.notion],
	},
	{
		id: "product-launch-post",
		title: "Write a Product Update from This Month's Deploys",
		description:
			"Review GitHub PRs and Linear tickets from this month, then write a customer-facing product update post",
		prompt:
			"Review all merged GitHub pull requests and completed Linear tickets from this month. Identify user-facing features, improvements, and bug fixes — skip internal refactors. Write a customer-facing product update post with clear sections: new features, improvements, and fixes. Include screenshots or code snippets where relevant. Save the draft to Notion",
		category: "Marketing",
		integrations: [I.github, I.linear, I.notion],
	},
	{
		id: "email-drip-optimizer",
		title: "Which Drip Emails Need Rewriting?",
		description:
			"Review email sequence performance — find high and low CTR emails, suggest subject line and copy changes",
		prompt:
			"Pull performance data for our email drip sequences — open rate, CTR, and unsubscribe rate for each email in the flow. Identify the top 3 performers (what's working?) and the bottom 3 (what's not?). For each underperformer, diagnose the likely issue (subject line, copy length, CTA, send timing) and draft a rewritten version. Save the analysis and new drafts to Notion and post findings to #marketing in Slack",
		category: "Marketing",
		integrations: [I.mailchimp, I.notion, I.slack],
	},
	{
		id: "youtube-influencer-prospecting",
		title: "Find YouTube Influencers to Partner With",
		description:
			"Search for relevant YouTube creators, build a lead list with subscriber count, contact info, and engagement stats",
		prompt:
			"Search for YouTube influencers in our industry with 10k-100k subscribers. For each, find: channel name, subscriber count, average views per video, content topics, and any contact info (email from about page, social links). Build a ranked lead list of the top 20 prospects in Google Sheets. Post the list to #marketing in Slack",
		category: "Marketing",
		integrations: [I.youtube, I.tavily, I["google-sheets"]],
	},
	{
		id: "monthly-content-plan",
		title: "Plan Next Month's Newsletter + Social Posts",
		description:
			"Build a content calendar — weekly newsletter topics and social posts using context from Slack, GitHub, and analytics",
		prompt:
			"Create a content plan for next month. Check Slack for recent team discussions and wins, GitHub for features shipped and upcoming, and Google Analytics for our top-performing content. Plan: 4 weekly newsletter emails (topic, subject line, key points) and 4 social media posts (hook, content angle, CTA). Make sure the content ties into what we're actually building and shipping. Save the full plan to Notion",
		category: "Marketing",
		integrations: [I.slack, I.github, I.notion],
	},
	{
		id: "cro-analysis",
		title: "Where Are We Losing Visitors on the Website?",
		description:
			"Analyze GA and PostHog data to find conversion drop-offs and suggest specific experiments to test",
		prompt:
			"Review our website data from Google Analytics (traffic sources, bounce rates, top pages, user flow) and PostHog (session recordings patterns, funnel drop-offs). Identify the 3 biggest conversion bottlenecks — where are visitors dropping off before signup or purchase? For each bottleneck, suggest a specific experiment to test (copy change, layout change, CTA change) with a hypothesis. Save the CRO report to Notion",
		category: "Marketing",
		integrations: [I["google-analytics"], I.posthog, I.notion],
	},
	{
		id: "technical-blog-writer",
		title: "Write a Technical Blog Post with Code Examples",
		description:
			"Review a recent feature or repo and write a developer-focused blog post with working code examples",
		prompt:
			"Review our latest feature shipped this week in GitHub — the merged PRs, code changes, and Linear tickets for context. Write a technical blog post explaining what we built, why, and how. Include code examples, architecture decisions, and lessons learned. Target developers who might use our product or find the approach interesting. Save the draft to Notion",
		category: "Marketing",
		integrations: [I.github, I.linear, I.notion],
	},
	{
		id: "semrush-ranking-tracker",
		title: "Which Keywords Are We Gaining or Losing?",
		description: "Track keyword position changes week-over-week and flag pages that need attention",
		prompt:
			"Pull our Semrush keyword rankings and compare to last week. Which keywords moved up 5+ positions (wins) and which dropped 5+ (losses)? For each loser, check if the page content is outdated, if competitors published something better, or if there's a technical issue. Update the ranking tracker in Google Sheets and post wins and action items to #marketing in Slack",
		category: "Marketing",
		integrations: [I.semrush, I["google-sheets"], I.slack],
	},
	{
		id: "mailchimp-list-health",
		title: "Clean Up Our Email List to Improve Deliverability",
		description:
			"Find inactive subscribers, high-bounce segments, and spam traps dragging down deliverability",
		prompt:
			"Analyze our Mailchimp subscriber list health. Identify: subscribers who haven't opened an email in 6+ months, segments with bounce rates above 5%, and any email addresses that look like spam traps. Calculate how much we're paying for dead subscribers. Recommend a cleanup plan — who to re-engage, who to remove — and post the analysis to #marketing in Slack",
		category: "Marketing",
		integrations: [I.mailchimp, I.slack],
	},
	{
		id: "sendgrid-domain-reputation",
		title: "Is Our Email Domain Reputation Healthy?",
		description:
			"Check domain reputation, bounce rates, and spam complaints to prevent deliverability issues",
		prompt:
			"Check our SendGrid domain reputation score, bounce rate, spam complaint rate, and delivery rate trends over the past 30 days. Flag if any metric is trending in the wrong direction. If bounce rate is above 2% or spam complaints above 0.1%, investigate which campaigns or segments are causing it. Post the deliverability health report to #marketing in Slack",
		category: "Marketing",
		integrations: [I.sendgrid, I.slack],
	},
	{
		id: "search-console-content-decay",
		title: "Which Pages Are Losing Rankings?",
		description: "Find pages with declining clicks and impressions that need content refreshes",
		prompt:
			"Pull Google Search Console data for the past 3 months. Identify pages where clicks or impressions dropped more than 20% month-over-month — these are decaying. For each, check the current ranking position and the queries driving traffic. Prioritize by traffic impact and save a content refresh plan to Notion with specific update recommendations. Post the top 5 priorities to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-search-console"], I.notion, I.slack],
	},
	{
		id: "seo-keyword-blog-pipeline",
		title: "Find Low-Competition Keywords and Write Blog Posts to Rank For Them",
		description:
			"Use Keyword Planner and Search Console to find easy-win keywords, then draft and publish SEO blog posts",
		prompt:
			"Pull our current keyword performance from Google Search Console — which queries are we already ranking for on page 2-3 (positions 8-30) and which have high impressions but low CTR? Then use Google Ads Keyword Planner to find related low-competition, decent-volume keywords we're not targeting yet. Cross-reference both lists to identify the best opportunities: keywords where we have some existing authority but no dedicated content. For the top 5 keyword opportunities, draft full SEO-optimized blog posts with proper headings, meta descriptions, and internal linking suggestions. Publish the drafts to Notion and post the keyword strategy summary to #marketing in Slack",
		category: "Marketing",
		integrations: [I["google-ads"], I["google-search-console"], I.notion, I.slack],
	},
	{
		id: "ahrefs-competitor-backlinks",
		title: "Where Are Competitors Getting Links We're Not?",
		description:
			"Find backlink sources linking to competitors but not us — these are outreach targets",
		prompt:
			"Use Ahrefs to compare our backlink profile against our top 3 competitors. Find domains that link to 2+ competitors but not us — these are warm outreach targets who already link to similar content. Rank by domain authority and relevance. Build a prospect list with domain, DA, linking page, and a suggested outreach angle. Save to Google Sheets and post the top 10 targets to #marketing in Slack",
		category: "Marketing",
		integrations: [I.ahrefs, I["google-sheets"], I.slack],
	},
	{
		id: "klaviyo-segment-revenue",
		title: "Which Customer Segments Drive the Most Revenue?",
		description:
			"Break down revenue by customer segment and identify which segments to double down on",
		prompt:
			"Pull Klaviyo revenue data broken down by customer segment — new vs returning, acquisition source, geography, and purchase frequency. Identify which segments have the highest LTV and which are underperforming. Recommend segments to invest more in (targeted campaigns) and segments to deprioritize. Update the segment analysis in Google Sheets and post key findings to #marketing in Slack",
		category: "Marketing",
		integrations: [I.klaviyo, I["google-sheets"], I.slack],
	},
	{
		id: "brevo-list-cleanup",
		title: "Remove Dead Subscribers to Save Money",
		description: "Find subscribers who never engage and estimate savings from removing them",
		prompt:
			"Analyze our Brevo subscriber list for contacts who haven't opened a single email in 6+ months. Calculate how many dead subscribers we have, what they cost us per month, and the impact on our deliverability metrics. Recommend a sunset flow — one last re-engagement email before removal. Post the cleanup plan with estimated monthly savings to #marketing in Slack",
		category: "Marketing",
		integrations: [I.brevo, I.slack],
	},
	{
		id: "webflow-seo-audit",
		title: "Which Pages Need SEO Fixes?",
		description: "Audit Webflow site for missing meta descriptions, broken links, and SEO issues",
		prompt:
			"Audit our Webflow site for SEO issues: pages missing meta descriptions or titles, duplicate H1 tags, missing alt text on images, broken internal links, and pages not indexed. Prioritize fixes by traffic impact. Save the full audit to Google Sheets with specific fix instructions for each issue",
		category: "Marketing",
		integrations: [I.webflow, I["google-sheets"]],
	},
	{
		id: "contentful-publishing-pipeline",
		title: "What Content Is Ready to Publish?",
		description:
			"Review CMS pipeline — what's in draft, what's approved, what's overdue for publishing",
		prompt:
			"Review our Contentful CMS entries: list everything in draft, in review, and approved-but-not-published. Flag any content that's been in draft for more than 2 weeks (stuck) or approved for more than 3 days without publishing (forgotten). Post a publishing pipeline summary to #marketing in Slack with action items for each stuck piece",
		category: "Marketing",
		integrations: [I.contentful, I.slack],
	},
	{
		id: "discord-engagement-playbook",
		title: "Start Conversations in Discord Communities Where Our Users Hang Out",
		description:
			"Find active Discord communities in our space and draft engagement messages to participate authentically",
		prompt:
			"Research Discord communities where our target users hang out. For each community, identify the most active channels and recent discussion topics relevant to our product. Draft 5 conversation starters or helpful responses we can post that provide genuine value (not promotion). Save the engagement playbook to Notion with community links, channel recommendations, and drafted messages",
		category: "Marketing",
		integrations: [I.discord, I.notion],
	},
	{
		id: "reddit-community-engagement",
		title: "Find Reddit Threads Where We Can Genuinely Help",
		description:
			"Search Reddit for questions and discussions where our expertise adds value — draft helpful responses",
		prompt:
			"Search Reddit for recent threads where people are asking questions we can genuinely help with — problems our product solves, topics in our expertise area, or comparisons where we're relevant. For each thread, draft a helpful response that provides real value without being spammy or promotional. Save the thread list and drafted responses to Notion, and post the top 5 opportunities to #marketing in Slack",
		category: "Marketing",
		integrations: [I.reddit, I.notion, I.slack],
	},
	{
		id: "discord-onboarding-improvement",
		title: "Which New Members Are Going Silent After Joining?",
		description:
			"Identify new Discord members who joined but never engaged — draft welcome messages to activate them",
		prompt:
			"Check our Discord server for members who joined in the last 2 weeks but haven't posted any messages. Segment them by join date and any roles they selected. Draft personalized welcome DMs for each that reference their interests and suggest specific channels or threads to check out. Post a summary of the silent member count and engagement rate to #community in Slack",
		category: "Marketing",
		integrations: [I.discord, I.slack],
	},
	{
		id: "competitor-ad-teardown",
		title: "What Ads Are Our Competitors Running?",
		description:
			"Research competitor ad campaigns across platforms, compare to ours, and draft improvement proposals",
		prompt:
			"Research what Google and Facebook/Instagram ads our top 3 competitors are currently running — ad copy, creative themes, landing pages, and offers. Compare their messaging and positioning to our current campaigns. Identify gaps in our approach and draft 3 specific ad improvement proposals with new copy and targeting suggestions. Save the full competitive ad analysis to Notion",
		category: "Marketing",
		integrations: [I.tavily, I["google-ads"], I["meta-ads"], I.notion],
	},
	{
		id: "pr-media-outreach",
		title: "Find Journalists and Bloggers to Pitch Our Story",
		description:
			"Research relevant journalists and bloggers, build a media list, and draft personalized pitches",
		prompt:
			"Research journalists and bloggers who cover our industry or have written about competitors. Build a media list in Google Sheets with their name, publication, recent articles, and contact info. Draft personalized pitch emails for each that reference their recent work and explain why our story is relevant to their audience. Send the top 5 draft pitches to Gmail as drafts",
		category: "Marketing",
		integrations: [I.tavily, I["google-sheets"], I.gmail],
	},
	// ── Sales ──
	{
		id: "hubspot-deal-followups",
		title: "Revive Stalled Deals",
		description: "Flag stalled deals and draft re-engagement emails",
		prompt:
			"Check HubSpot for deals with no activity in 2 weeks, flag them as at-risk, and draft re-engagement emails in Gmail for each",
		category: "Sales",
		integrations: [I.hubspot, I.gmail],
	},
	{
		id: "pipedrive-pipeline-report",
		title: "Weekly Pipeline Report",
		description: "Summarize pipeline health into a spreadsheet",
		prompt:
			"Pull this week's Pipedrive pipeline data (deals by stage, total value, win rate) and update the Google Sheets pipeline report",
		category: "Sales",
		integrations: [I.pipedrive, I["google-sheets"]],
	},
	{
		id: "apollo-lead-research",
		title: "Research & Reach New Leads",
		description: "Research new leads and draft personalized outreach",
		prompt:
			"For new leads added to Apollo this week, research their company and role, then draft personalized outreach emails in Gmail",
		category: "Sales",
		integrations: [I.apollo, I.gmail],
	},
	{
		id: "salesforce-stalled-deals",
		title: "Unstick Stuck Opportunities",
		description: "Identify stalled opportunities and draft follow-ups",
		prompt:
			"Check Salesforce for opportunities stuck in the same stage for 3+ weeks and draft follow-up emails in Gmail with context from the last activity",
		category: "Sales",
		integrations: [I.salesforce, I.gmail],
	},
	{
		id: "close-crm-weekly-pipeline",
		title: "Weekly Pipeline Summary",
		description: "Post a weekly pipeline summary to Slack",
		prompt:
			"Every Monday, pull the Close CRM pipeline summary (new leads, deals won/lost, total pipeline value) and post it to #sales in Slack",
		category: "Sales",
		integrations: [I["close-crm"], I.slack],
	},
	{
		id: "attio-crm-weekly-digest",
		title: "Weekly CRM Digest",
		description: "Summarize CRM activity and new deals for the week",
		prompt:
			"Every Monday, pull the week's CRM activity from Attio — new deals, status changes, and notes — and post a digest to #sales in Slack",
		category: "Sales",
		integrations: [I.attio, I.slack],
	},
	{
		id: "calendly-meeting-prep",
		title: "Prep for Tomorrow's Meetings",
		description: "Research upcoming meeting attendees and draft briefings",
		prompt:
			"Look at my Calendly meetings for tomorrow, research each attendee's company and role, and send me a prep briefing via Gmail",
		category: "Sales",
		integrations: [I.calendly, I.gmail],
	},
	{
		id: "zoho-crm-activity-report",
		title: "Sales Activity Report",
		description: "Summarize CRM activity and highlight deals nearing close",
		prompt:
			"Pull this week's Zoho CRM activity — calls, emails, meetings — and highlight deals nearing close",
		category: "Sales",
		integrations: [I.zoho],
	},
	{
		id: "dynamics365-pipeline-review",
		title: "Draft Follow-Ups for Stuck Deals",
		description:
			"Find deals stuck in the same stage for 2+ weeks, draft personalized follow-up emails with deal context",
		prompt:
			"Review our Dynamics 365 pipeline for deals stuck in the same stage for 2+ weeks. For each one, pull the last activity and deal notes, then draft a personalized follow-up email in Gmail. Post a summary of at-risk deals to #sales in Slack",
		category: "Sales",
		integrations: [I.dynamics365, I.gmail, I.slack],
	},
	{
		id: "gong-call-insights",
		title: "What Objections Keep Killing Deals?",
		description: "Analyze call recordings for recurring objections and build a response playbook",
		prompt:
			"Review this week's Gong call recordings and identify the top 5 recurring objections from prospects. For each objection, draft a recommended response. Save the objection playbook to Notion and post a summary to #sales in Slack",
		category: "Sales",
		integrations: [I.gong, I.notion, I.slack],
	},
	{
		id: "affinity-relationship-map",
		title: "Relationship Health Check",
		description: "Summarize key relationship updates and flag stale contacts",
		prompt:
			"Summarize our key relationship updates in Affinity this week and flag any VIP contacts with no recent touchpoints",
		category: "Sales",
		integrations: [I.affinity],
	},
	{
		id: "docusign-contract-status",
		title: "Chase Unsigned Contracts",
		description: "Check for contracts awaiting signature and send reminders",
		prompt:
			"Check DocuSign for any contracts awaiting signature for more than 3 days and send a reminder",
		category: "Sales",
		integrations: [I.docusign],
	},
	{
		id: "pandadoc-proposal-tracker",
		title: "Follow Up on Proposals",
		description: "Review open proposals and flag unviewed ones",
		prompt: "Review open PandaDoc proposals and flag any that haven't been viewed in over a week",
		category: "Sales",
		integrations: [I.pandadoc],
	},
	{
		id: "hubspot-lead-scoring",
		title: "How Many Leads Came In This Month?",
		description:
			"Count and score new leads by source, update tracking sheet, and alert sales on high-value ones",
		prompt:
			"Pull all new leads from HubSpot this month, break them down by source (organic, paid, referral, etc.), and score them by engagement level. Update the lead tracking Google Sheet and post a summary of high-value leads to #sales in Slack",
		category: "Sales",
		integrations: [I.hubspot, I["google-sheets"], I.slack],
	},
	{
		id: "salesforce-pipeline-forecast",
		title: "What Will We Actually Close This Quarter?",
		description:
			"Analyze pipeline by stage, win probability, and rep activity to build a realistic forecast",
		prompt:
			"Pull our Salesforce pipeline for this quarter. For each deal, analyze the stage, days in stage, recent activity, and historical win rate for that rep. Build a realistic revenue forecast in Google Sheets — separate 'likely', 'possible', and 'unlikely' buckets. Post the forecast summary to #sales in Slack",
		category: "Sales",
		integrations: [I.salesforce, I["google-sheets"], I.slack],
	},
	{
		id: "linkedin-prospecting",
		title: "Find Decision-Makers on LinkedIn to Reach Out To",
		description:
			"Search for ideal customer profiles on LinkedIn, build a prospect list, and draft personalized connection messages",
		prompt:
			"Search for people on LinkedIn who match our ideal customer profile — target roles like CTO, VP Engineering, Head of Growth at companies in our target segment. For the top 20 matches, research their company, recent posts, and background. Build a prospect list in Google Sheets with: name, title, company, company size, LinkedIn URL, and a personalized connection message for each. Post the list to #sales in Slack",
		category: "Sales",
		integrations: [I.linkedin, I.tavily, I["google-sheets"]],
	},
	{
		id: "twitter-prospecting",
		title: "Find Relevant People on X to Connect With",
		description:
			"Find active X accounts in our target market who discuss relevant topics, and draft personalized outreach",
		prompt:
			"Search X/Twitter for people actively discussing topics related to our product — look for founders, operators, and decision-makers who tweet about our problem space. For the top 20 accounts, note: handle, follower count, bio, recent relevant tweets, and engagement level. Draft a personalized DM or reply for each that references their recent content. Build the prospect list in Google Sheets and post to #sales in Slack",
		category: "Sales",
		integrations: [I.twitter, I.tavily, I["google-sheets"]],
	},
	{
		id: "cold-email-sequences",
		title: "Draft a Cold Email Sequence for Our ICP",
		description:
			"Research target accounts and write a 3-email outreach sequence personalized to each prospect",
		prompt:
			"For prospects in our lead list (or research 10 new target companies that match our ICP), research each company: what they do, recent news, tech stack, team size. Then draft a 3-email cold outreach sequence for each: (1) initial outreach referencing something specific about their company, (2) follow-up with a relevant case study or data point, (3) break-up email with a direct ask. Save all drafts in Google Sheets organized by prospect",
		category: "Sales",
		integrations: [I.apollo, I.tavily, I["google-sheets"]],
	},
	{
		id: "investor-outreach",
		title: "Find Pre-Seed Angels and Draft Pitch Emails",
		description:
			"Research angel investors who invest in our space, find their contact info, and draft customized intro emails",
		prompt:
			"Research pre-seed and angel investors who have invested in companies similar to ours — same industry, stage, and business model. For each investor, find: name, fund or affiliation, portfolio companies, investment thesis, LinkedIn URL, and email if available. Draft a personalized intro email for each that references their relevant portfolio companies and explains why our company fits their thesis. Build a list of 15 investors in Google Sheets and save the email drafts alongside each",
		category: "Sales",
		integrations: [I.tavily, I["google-sheets"], I.gmail],
	},
	{
		id: "pipedrive-deal-velocity",
		title: "How Long Are Deals Taking to Close?",
		description: "Analyze deal velocity by stage, rep, and deal size to find where deals stall",
		prompt:
			"Pull Pipedrive deal data from the past 3 months. Calculate average time in each pipeline stage, broken down by rep and deal size. Identify which stage has the longest dwell time — that's our bottleneck. Compare top-performing reps vs average to find what they do differently. Update the deal velocity tracker in Google Sheets and post insights to #sales in Slack",
		category: "Sales",
		integrations: [I.pipedrive, I["google-sheets"], I.slack],
	},
	{
		id: "hubspot-email-sequences",
		title: "Which Sales Sequences Get Replies?",
		description:
			"Analyze email sequence performance — open rates, reply rates, and best-performing steps",
		prompt:
			"Pull HubSpot sales sequence data for the past month. For each sequence, show: enrollment count, open rate, reply rate, meeting booked rate, and best-performing email step. Identify sequences with high opens but low replies (messaging problem) vs low opens (subject line problem). Recommend specific improvements for the bottom 3 sequences. Update the tracker in Google Sheets and post to #sales in Slack",
		category: "Sales",
		integrations: [I.hubspot, I["google-sheets"], I.slack],
	},
	{
		id: "salesforce-rep-performance",
		title: "Which Reps Are Hitting Quota?",
		description:
			"Track quota attainment by rep, identify who needs coaching, and spot pipeline gaps",
		prompt:
			"Pull Salesforce quota attainment data for each rep this quarter. Show: quota, closed-won, pipeline coverage ratio, win rate, and average deal size. Flag reps below 60% attainment with low pipeline coverage — they need urgent attention. Identify reps with high activity but low win rates (coaching opportunity) vs those with few deals but high win rates (capacity for more). Update the scorecard in Google Sheets and post to #sales in Slack",
		category: "Sales",
		integrations: [I.salesforce, I["google-sheets"], I.slack],
	},
	{
		id: "close-crm-win-loss",
		title: "Why Are We Losing Deals?",
		description:
			"Analyze closed-lost deals for patterns — is it pricing, competition, timing, or fit?",
		prompt:
			"Pull all closed-lost deals from Close CRM in the past 60 days. Categorize loss reasons: pricing too high, lost to competitor, no decision made, bad timing, or not a fit. Identify the #1 reason and check if it's concentrated in a specific segment, deal size, or rep. Document patterns and suggested fixes in Notion and post the top findings to #sales in Slack",
		category: "Sales",
		integrations: [I["close-crm"], I.notion, I.slack],
	},
	{
		id: "zoho-lead-response-time",
		title: "How Fast Are We Responding to Leads?",
		description: "Measure lead response time by source and rep — speed matters for conversion",
		prompt:
			"Pull Zoho CRM data on lead response times for the past month. Calculate average time from lead creation to first contact, broken down by lead source and assigned rep. Flag any leads that went more than 4 hours without a response. Research shows response time is the #1 predictor of conversion — identify our slowest areas and post improvement recommendations to #sales in Slack",
		category: "Sales",
		integrations: [I.zoho, I.slack],
	},
	{
		id: "attio-deal-scoring",
		title: "Score Our Pipeline by Win Likelihood",
		description: "Analyze deal signals to predict which deals will close and which are at risk",
		prompt:
			"Pull our Attio pipeline data and score each open deal by win likelihood. Look at signals: recent activity (last touch), stakeholder engagement, deal age vs average cycle time, and stage progression speed. Categorize deals as 'hot' (likely to close), 'warm' (needs nurture), or 'cold' (at risk). Update the deal scores in Google Sheets and post the pipeline health summary to #sales in Slack",
		category: "Sales",
		integrations: [I.attio, I["google-sheets"], I.slack],
	},
	{
		id: "apollo-icp-enrichment",
		title: "Enrich Our Lead List with Company Data",
		description:
			"Take existing leads and fill in missing company data — size, funding, tech stack, decision-makers",
		prompt:
			"Pull our current lead list from Apollo and identify records with missing data (company size, funding stage, tech stack, decision-maker contacts). For each incomplete record, research and enrich: company employee count, latest funding round, key technologies they use, and additional decision-maker contacts. Update the enriched data in Google Sheets and post a summary of what was added to #sales in Slack",
		category: "Sales",
		integrations: [I.apollo, I["google-sheets"], I.slack],
	},
	{
		id: "docusign-revenue-at-risk",
		title: "How Much Revenue Is Stuck in Unsigned Contracts?",
		description:
			"Calculate total value of pending contracts and prioritize follow-ups by deal size",
		prompt:
			"Pull all pending DocuSign envelopes that haven't been signed. For each, pull the contract value, days pending, and recipient. Calculate total revenue at risk. Prioritize by contract value — the biggest deals need follow-up first. Draft a personalized reminder for each high-value contract and send via Gmail. Post the total at-risk revenue to #sales in Slack",
		category: "Sales",
		integrations: [I.docusign, I.gmail, I.slack],
	},
	{
		id: "pandadoc-proposal-win-rate",
		title: "Which Proposal Templates Win the Most?",
		description:
			"Analyze proposal outcomes by template, deal size, and send timing to improve win rates",
		prompt:
			"Pull PandaDoc proposal data from the past 3 months. For each proposal template, calculate: send count, view rate, time to first view, and win rate. Identify which templates convert best and which consistently lose. Check if send timing (day of week, time of day) affects view rates. Document findings in Notion and post the top insights to #sales in Slack",
		category: "Sales",
		integrations: [I.pandadoc, I.notion, I.slack],
	},
	{
		id: "calendly-no-show-tracker",
		title: "How Many Meetings Are No-Shows?",
		description: "Track no-show rates by meeting type and source to reduce wasted sales time",
		prompt:
			"Pull Calendly booking data for the past month. Calculate no-show rates broken down by meeting type, lead source, and day of week. Identify which meeting types or sources have the worst no-show rates. Recommend fixes — confirmation email timing, SMS reminders, overbooking slots with high no-show rates. Post the analysis to #sales in Slack",
		category: "Sales",
		integrations: [I.calendly, I.slack],
	},
	{
		id: "affinity-warm-intros",
		title: "Who Can Make a Warm Intro to Our Target Accounts?",
		description:
			"Cross-reference target accounts with your relationship network to find warm intro paths",
		prompt:
			"Pull our target account list and cross-reference against our Affinity relationship data. For each target account, check if anyone in our network has a connection there — a mutual contact, former colleague, or investor relationship. Rank intro paths by relationship strength. Build an intro request tracker in Google Sheets with: target company, connector name, relationship context, and suggested ask. Post the top 10 warm intro opportunities to #sales in Slack",
		category: "Sales",
		integrations: [I.affinity, I["google-sheets"], I.slack],
	},
	// ── Product ──
	{
		id: "linear-bug-triage",
		title: "Morning Bug Triage",
		description: "Check for new bugs and post a prioritized summary to Slack",
		prompt:
			"Every morning, check Linear for new bug reports, categorize them by severity, and send a prioritized summary to #engineering in Slack",
		category: "Product",
		integrations: [I.linear, I.slack],
	},
	{
		id: "posthog-funnel-analysis",
		title: "Find Signup Drop-offs",
		description: "Analyze signup funnel and document drop-off recommendations",
		prompt:
			"Analyze our signup funnel in PostHog, identify the biggest drop-off points, and write up 3 recommendations in Notion",
		category: "Product",
		integrations: [I.posthog, I.notion],
	},
	{
		id: "jira-sprint-summary",
		title: "Sprint Status & Blockers",
		description: "Summarize the current sprint status and blockers",
		prompt:
			"Review the current Jira sprint, summarize completed vs remaining story points, list any blocked tickets, and identify at-risk items",
		category: "Product",
		integrations: [I.jira],
	},
	{
		id: "github-pr-changelog",
		title: "Weekly PR Changelog",
		description: "Generate a changelog from merged PRs and post to Slack",
		prompt:
			"List all GitHub PRs merged this week, categorize them (feature, fix, chore), and post a formatted changelog to #product in Slack",
		category: "Product",
		integrations: [I.github, I.slack],
	},
	{
		id: "asana-sprint-report",
		title: "Sprint Progress Report",
		description: "Summarize task progress and flag overdue items",
		prompt:
			"Look at our Asana board, summarize task progress for this sprint, flag anything overdue by more than a week, and post the report to #product in Slack",
		category: "Product",
		integrations: [I.asana, I.slack],
	},
	{
		id: "clickup-task-review",
		title: "Flag Overdue Tasks",
		description: "Review tasks and flag blockers or overdue items",
		prompt:
			"Review our ClickUp workspace for overdue tasks and items without assignees, then post a summary with action items to #product in Slack",
		category: "Product",
		integrations: [I.clickup, I.slack],
	},
	{
		id: "sentry-error-digest",
		title: "Triage New Errors",
		description: "Summarize new errors and create tickets for critical ones",
		prompt:
			"Summarize today's new Sentry errors, group them by frequency, and create Linear tickets for any critical or high-frequency issues",
		category: "Product",
		integrations: [I.sentry, I.linear],
	},
	{
		id: "gitlab-mr-summary",
		title: "Write This Week's Release Notes",
		description: "Generate customer-facing release notes from merged MRs and save to Notion",
		prompt:
			"List all GitLab merge requests merged this week. Categorize by type (feature, fix, improvement) and write customer-facing release notes — skip internal refactors. Save the release notes to Notion",
		category: "Product",
		integrations: [I.gitlab, I.notion],
	},
	{
		id: "bitbucket-pr-review",
		title: "Nudge Stale PRs",
		description: "Review open PRs older than 3 days and remind the team",
		prompt:
			"Review open Bitbucket pull requests older than 3 days and remind the team in Slack to review them",
		category: "Product",
		integrations: [I.bitbucket, I.slack],
	},
	{
		id: "monday-sprint-status",
		title: "What's At Risk This Sprint?",
		description:
			"Analyze velocity vs remaining work to flag items that won't make the sprint deadline",
		prompt:
			"Review our Monday board's sprint status. Calculate current velocity based on completed items this week vs remaining work. Flag any items at risk of missing the sprint deadline with reasons why, and post the analysis to #product in Slack",
		category: "Product",
		integrations: [I.monday, I.slack],
	},
	{
		id: "wrike-project-health",
		title: "Which Tasks Are Holding Up the Rest?",
		description:
			"Identify blocking tasks causing cascading delays and recommend how to unblock them",
		prompt:
			"Review our Wrike projects and identify tasks that are overdue AND blocking other tasks downstream. Calculate the cascading delay impact for each blocker and recommend actions to unblock them. Post the analysis to #product in Slack",
		category: "Product",
		integrations: [I.wrike, I.slack],
	},
	{
		id: "shortcut-iteration-report",
		title: "Prep the Sprint Retro with Real Data",
		description:
			"Analyze velocity trends, completion rates, and carry-over to prepare a data-driven retro",
		prompt:
			"Pull data from our last 3 Shortcut iterations — velocity, completion rate, stories carried over, and cycle time trends. Prepare a retro document in Notion highlighting what's improving, what's declining, and specific patterns. Post the summary to #engineering in Slack",
		category: "Product",
		integrations: [I.shortcut, I.notion, I.slack],
	},
	{
		id: "pagerduty-incident-summary",
		title: "Why Do We Keep Getting the Same Alerts?",
		description:
			"Identify repeat incident patterns, find root causes, and create tickets to fix them permanently",
		prompt:
			"Review this week's PagerDuty incidents and group them by service and alert type. Identify any alerts that fired 3+ times — these are repeat offenders. For each, draft a root cause hypothesis and create a Linear ticket to investigate a permanent fix. Post the incident pattern report to #engineering in Slack",
		category: "Product",
		integrations: [I.pagerduty, I.linear, I.slack],
	},
	{
		id: "datadog-alert-digest",
		title: "Which Alerts Are Real vs Noise?",
		description:
			"Categorize alerts by signal vs noise, create tickets for real issues, and recommend tuning for noisy ones",
		prompt:
			"Review Datadog alerts from the last 24 hours. Categorize each as 'real issue', 'noise', or 'needs investigation'. Create Linear tickets for real issues with context from the alert. For noisy alerts, recommend threshold adjustments or muting. Post the categorized summary to #engineering in Slack",
		category: "Product",
		integrations: [I.datadog, I.linear, I.slack],
	},
	{
		id: "posthog-error-triage",
		title: "Are Users Hitting Errors We Don't Know About?",
		description:
			"Find client-side errors affecting real users and create tickets for the worst ones",
		prompt:
			"Check PostHog for client-side errors and exceptions from the past 7 days. Rank by number of affected users and sessions. For the top 5, identify the page, browser, and likely cause. Create Linear tickets for any error affecting more than 50 users and post a summary to #engineering in Slack",
		category: "Product",
		integrations: [I.posthog, I.linear, I.slack],
	},
	{
		id: "sentry-asana-bug-pipeline",
		title: "Triage Today's Sentry Bugs into Asana",
		description:
			"Review new Sentry errors, deduplicate against existing tickets, and create Asana tasks for new ones",
		prompt:
			"Pull today's new Sentry errors and group by issue type. Check Asana for existing tickets to avoid duplicates. For genuinely new issues, create Asana tasks with error details, affected users count, and stack trace summary. Post a triage summary to #engineering in Slack",
		category: "Product",
		integrations: [I.sentry, I.asana, I.slack],
	},
	{
		id: "jira-backlog-groomer",
		title: "Clean Up Stale Jira Tickets",
		description:
			"Find tickets older than 90 days with no recent activity and recommend close, merge, or reprioritize",
		prompt:
			"Scan our Jira backlog for tickets older than 90 days with no comments or status changes in the last 30 days. For each, recommend whether to close (no longer relevant), merge (duplicate), or reprioritize (still valid but forgotten). Post the grooming report to #product in Slack",
		category: "Product",
		integrations: [I.jira, I.slack],
	},
	{
		id: "github-security-audit",
		title: "Any Security Vulnerabilities in Our Repos?",
		description:
			"Check GitHub repos for Dependabot alerts and security advisories, create tickets for critical ones",
		prompt:
			"Check our GitHub repositories for open Dependabot alerts and security advisories. Prioritize by severity (critical, high, medium). Create Linear tickets for any critical or high-severity vulnerabilities with remediation steps. Post a security summary to #engineering in Slack",
		category: "Product",
		integrations: [I.github, I.linear, I.slack],
	},
	{
		id: "upsell-opportunities",
		title: "Which Customers Are Ready for an Upgrade?",
		description:
			"Find customers hitting plan limits or showing expansion signals and draft upgrade nudge emails",
		prompt:
			"Pull Stripe subscription data and identify customers showing upgrade signals: approaching usage limits, frequently hitting rate limits, on the same plan for 6+ months with growing usage, or teams that have added members recently. Cross-reference with HubSpot activity to find engaged accounts. Draft personalized upgrade nudge emails for the top 10 candidates that highlight features they'd unlock. Save drafts to Gmail",
		category: "Sales",
		integrations: [I.stripe, I.hubspot, I.gmail],
	},
	{
		id: "webinar-followup",
		title: "Follow Up with Webinar Attendees Who Didn't Convert",
		description:
			"Segment webinar attendees by engagement level and draft targeted follow-up sequences",
		prompt:
			"Pull attendee data from our latest Zoom webinar — who attended live, who watched the recording, who dropped off early, and who stayed for Q&A. Cross-reference with our CRM to identify who's already a customer vs prospect. Segment prospects by engagement level and draft targeted follow-up emails for each segment: high-engagement (stayed for Q&A), medium (watched most), and low (dropped early). Save the segmented list to Google Sheets and draft follow-up emails in Gmail",
		category: "Sales",
		integrations: [I.zoom, I.gmail, I["google-sheets"]],
	},
	// ── Operations ──
	{
		id: "slack-weekly-summary",
		title: "Weekly Team Recap",
		description: "Compile key channel highlights into Notion",
		prompt:
			"Every Friday, summarize the key discussions and decisions from #general, #product, and #engineering in Slack and post a weekly recap in Notion",
		category: "Operations",
		integrations: [I.slack, I.notion],
	},
	{
		id: "gmail-to-linear-tasks",
		title: "Turn Emails into Tasks",
		description: "Turn email threads into Linear tasks with requirements",
		prompt:
			"Read this email thread in Gmail, extract the requirements and acceptance criteria, and create a task in Linear with all the details",
		category: "Operations",
		integrations: [I.gmail, I.linear],
	},
	{
		id: "google-calendar-one-on-ones",
		title: "Schedule Team 1:1s",
		description: "Schedule recurring 1:1s with your team",
		prompt:
			"Schedule 30-minute 1:1 meetings with each of my direct reports for next month in Google Calendar, finding times that work for everyone",
		category: "Operations",
		integrations: [I["google-calendar"]],
	},
	{
		id: "notion-weekly-update",
		title: "Weekly Update Roundup",
		description: "Compile team updates and post to Slack",
		prompt:
			"Every Monday, compile the latest updates from our Notion team pages and post a consolidated weekly update to #team in Slack",
		category: "Operations",
		integrations: [I.notion, I.slack],
	},
	{
		id: "zendesk-ticket-summary",
		title: "Support Ticket Digest",
		description: "Summarize support tickets and flag recurring issues",
		prompt:
			"Summarize this week's Zendesk tickets by category, flag any recurring issues, and post the summary to #support in Slack",
		category: "Operations",
		integrations: [I.zendesk, I.slack],
	},
	{
		id: "intercom-customer-feedback",
		title: "Top Customer Complaints",
		description: "Analyze support conversations for common complaints",
		prompt:
			"Check our Intercom conversations from this week, identify the top 5 recurring complaints, and summarize them in a Notion page with suggested fixes",
		category: "Operations",
		integrations: [I.intercom, I.notion],
	},
	{
		id: "outlook-email-digest",
		title: "Daily Email Digest",
		description: "Summarize unread emails and flag action items",
		prompt:
			"Go through my unread Outlook emails from the last 24 hours, summarize the important ones, and list any action items I need to handle",
		category: "Operations",
		integrations: [I.outlook],
	},
	{
		id: "zoom-meeting-notes",
		title: "Meeting Notes & Action Items",
		description: "Summarize recent meetings and save notes to Notion",
		prompt:
			"Pull my Zoom meeting recordings from this week, summarize the key discussion points and action items, and save them as meeting notes in Notion",
		category: "Operations",
		integrations: [I.zoom, I.notion],
	},
	{
		id: "trello-board-summary",
		title: "Board Status & Stalled Cards",
		description: "Summarize board status and flag stalled cards",
		prompt:
			"Review our main Trello board, summarize cards by list, flag any cards that haven't moved in 2+ weeks, and post the summary to #team in Slack",
		category: "Operations",
		integrations: [I.trello, I.slack],
	},
	{
		id: "weekly-kpi-review",
		title: "Pull This Week's KPIs and Flag Changes",
		description:
			"Pull revenue, signups, churn, and support metrics from multiple tools and flag significant week-over-week changes",
		prompt:
			"Pull this week's KPIs — MRR and churn from Stripe, traffic from Google Analytics, and support ticket volume. Compare each metric week-over-week and flag any that changed more than 10%. Update the KPI tracker in Google Sheets and post the summary with trend arrows to #team in Slack",
		category: "Operations",
		integrations: [I.stripe, I["google-analytics"], I["google-sheets"], I.slack],
	},
	{
		id: "microsoft-teams-weekly-recap",
		title: "Weekly Discussion Recap",
		description: "Summarize key discussions and post action items",
		prompt:
			"Summarize key discussions from our Microsoft Teams channels this week and post action items",
		category: "Operations",
		integrations: [I["microsoft-teams"]],
	},
	{
		id: "whatsapp-business-outreach",
		title: "Follow Up with Customers",
		description: "Draft and send customer follow-up messages via WhatsApp",
		prompt:
			"Draft WhatsApp follow-up messages for customers who haven't responded in 3+ days and send them using our approved templates",
		category: "Operations",
		integrations: [I.whatsapp],
	},
	{
		id: "google-drive-file-cleanup",
		title: "Clean Up Stale Files",
		description: "Scan for files not accessed in 90 days",
		prompt: "Scan our Google Drive for files not accessed in 90 days and create a cleanup list",
		category: "Operations",
		integrations: [I["google-drive"]],
	},
	{
		id: "google-meet-prep",
		title: "Meeting Prep & Briefings",
		description: "Review upcoming meetings and prepare attendee briefs",
		prompt:
			"Check my Google Meet schedule for this week, list all meetings with attendees, and prepare a brief for each",
		category: "Operations",
		integrations: [I["google-meet"]],
	},
	{
		id: "google-tasks-weekly-review",
		title: "Reprioritize Your Week",
		description: "Flag overdue items and reprioritize for the week",
		prompt: "Review my Google Tasks, flag overdue items, and reprioritize for the coming week",
		category: "Operations",
		integrations: [I["google-tasks"]],
	},
	{
		id: "google-slides-deck-update",
		title: "Flag Outdated Slides",
		description: "Check team deck for outdated stats",
		prompt:
			"Check our Google Slides team deck for outdated stats and flag slides that need updating",
		category: "Operations",
		integrations: [I["google-slides"]],
	},
	{
		id: "airtable-data-review",
		title: "Fill In Missing CRM Data",
		description: "Find records with missing fields and auto-research the data to fill gaps",
		prompt:
			"Review our Airtable CRM base for records with missing company info, website, or contact details. Use Tavily to research and fill in the gaps. Post a summary of what was updated and what still needs manual review to #ops in Slack",
		category: "Operations",
		integrations: [I.airtable, I.tavily, I.slack],
	},
	{
		id: "confluence-page-audit",
		title: "Which Docs Are Outdated but Still Getting Read?",
		description:
			"Find high-traffic pages that haven't been updated in 6+ months — these mislead people",
		prompt:
			"Audit our Confluence space for pages that get regular views but haven't been edited in 6+ months. Rank by view count — the most-read stale pages are the highest priority to update. Post the list with page links to #team in Slack",
		category: "Operations",
		integrations: [I.confluence, I.slack],
	},
	{
		id: "todoist-team-productivity",
		title: "Who's Overloaded on the Team?",
		description: "Analyze task distribution and overdue items to spot team members who need help",
		prompt:
			"Pull Todoist task data for the team — assigned tasks, overdue items, and completion rates per person. Identify anyone with significantly more overdue items than average and suggest workload rebalancing. Post the analysis to #team in Slack",
		category: "Operations",
		integrations: [I.todoist, I.slack],
	},
	{
		id: "freshdesk-ticket-report",
		title: "Support Report & SLA Check",
		description: "Summarize tickets by category and flag SLA breaches",
		prompt: "Summarize this week's Freshdesk tickets by category and flag any with SLA breaches",
		category: "Operations",
		integrations: [I.freshdesk],
	},
	{
		id: "miro-board-summary",
		title: "Turn Miro Brainstorms into Tickets",
		description:
			"Extract ideas and decisions from Miro workshop boards and create structured tickets",
		prompt:
			"Review our Miro boards from this week's workshops. Extract key decisions, action items, and feature ideas. Create Linear tickets for each actionable item with descriptions and priorities from the brainstorm context. Post a summary of created tickets to #product in Slack",
		category: "Operations",
		integrations: [I.miro, I.linear, I.slack],
	},
	{
		id: "coda-doc-updates",
		title: "Weekly Doc Change Summary",
		description: "Check workspace for recently edited docs",
		prompt:
			"Check our Coda workspace for recently edited docs and summarize what's changed this week",
		category: "Operations",
		integrations: [I.coda],
	},
	{
		id: "intercom-feature-requests",
		title: "What Features Do Customers Keep Asking For?",
		description:
			"Extract and rank feature requests from support conversations, save to Notion for the product team",
		prompt:
			"Review Intercom conversations from the past month and extract feature requests. Group by theme, rank by frequency, and note which customer segments are asking. Save the prioritized feature request list to Notion and post the top 5 to #product in Slack",
		category: "Operations",
		integrations: [I.intercom, I.notion, I.slack],
	},
	{
		id: "zendesk-sla-crisis",
		title: "Any Support Tickets About to Breach SLA?",
		description: "Find tickets approaching SLA deadlines and escalate before they breach",
		prompt:
			"Check Zendesk for any open tickets within 2 hours of breaching their SLA. For each, include the ticket summary, priority, assigned agent, and time remaining. Post an urgent alert to #support in Slack so the team can act before the breach",
		category: "Operations",
		integrations: [I.zendesk, I.slack],
	},
	{
		id: "freshdesk-happiness-trend",
		title: "Are Support Satisfaction Scores Improving?",
		description:
			"Track CSAT trends over the past month and flag any agents or ticket types with declining scores",
		prompt:
			"Pull Freshdesk CSAT scores for the past 4 weeks. Track the overall trend and break down by agent and ticket category. Flag any agents or categories with declining satisfaction and recommend specific improvements. Post the trend report to #support in Slack",
		category: "Operations",
		integrations: [I.freshdesk, I.slack],
	},
	{
		id: "slack-channel-audit",
		title: "Which Slack Channels Should We Archive?",
		description:
			"Find inactive channels with no messages in 30+ days and recommend which to archive",
		prompt:
			"Audit our Slack workspace for channels with zero messages in the last 30 days. List each with its member count and last message date. Recommend which to archive (low members + inactive) vs which to keep (many members, seasonal use). Post the recommendations to #ops in Slack",
		category: "Operations",
		integrations: [I.slack],
	},
	{
		id: "calcom-meeting-optimizer",
		title: "Am I in Too Many Meetings?",
		description:
			"Analyze your meeting load, find patterns, and recommend which recurring meetings to cut",
		prompt:
			"Pull my Cal.com booking data for the past month. Calculate total meeting hours per week, busiest days, and average meeting length. Identify recurring meetings that could be async (status updates, FYIs) and recommend which to cut or shorten. Post the analysis to Slack",
		category: "Operations",
		integrations: [I.calcom, I.slack],
	},
	{
		id: "testimonial-collector",
		title: "Collect Fresh Customer Testimonials from Support Wins",
		description:
			"Mine positive support conversations for testimonial candidates and draft outreach requests",
		prompt:
			"Review recent Intercom conversations where customers expressed satisfaction, gratitude, or positive feedback about our product. Identify the top 10 candidates for testimonials based on enthusiasm level and their company profile. Draft personalized testimonial request messages for each that reference their specific positive experience. Save the candidate list and drafted requests to Notion, and post the top 5 opportunities to #marketing in Slack",
		category: "Operations",
		integrations: [I.intercom, I.notion, I.slack],
	},
	// ── Finance ──
	{
		id: "stripe-mrr-tracker",
		title: "Weekly MRR & Churn Update",
		description: "Pull Stripe metrics weekly into Google Sheets",
		prompt:
			"Every Monday, pull MRR, churn rate, and new customers from Stripe and update our tracking spreadsheet in Google Sheets",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"]],
	},
	{
		id: "quickbooks-budget-alerts",
		title: "Budget Overspend Alerts",
		description: "Monitor spending and alert when categories exceed budget",
		prompt:
			"Check QuickBooks weekly and alert me on Slack if any expense category is over 80% of its monthly budget",
		category: "Finance",
		integrations: [I.quickbooks, I.slack],
	},
	{
		id: "shopify-sales-report",
		title: "Weekly Sales Report",
		description: "Summarize Shopify sales data into Google Sheets",
		prompt:
			"Every Monday, pull last week's Shopify sales (revenue, orders, top products, refunds) and update the Google Sheets sales report",
		category: "Finance",
		integrations: [I.shopify, I["google-sheets"]],
	},
	{
		id: "xero-invoice-alerts",
		title: "Chase Overdue Invoices",
		description: "Flag overdue invoices and send payment reminders",
		prompt:
			"Check Xero for invoices overdue by more than 7 days, list the total outstanding amount, and post a reminder to #finance in Slack",
		category: "Finance",
		integrations: [I.xero, I.slack],
	},
	{
		id: "brex-expense-review",
		title: "Review Team Card Spend",
		description: "Review card transactions and categorize spending by team",
		prompt:
			"Review this month's Brex card transactions, flag any over $500, and categorize spending by team",
		category: "Finance",
		integrations: [I.brex],
	},
	{
		id: "mixpanel-revenue-analytics",
		title: "Where Are We Losing Revenue in the Funnel?",
		description:
			"Map revenue events through the funnel, estimate dollar impact at each drop-off, and recommend fixes",
		prompt:
			"Pull Mixpanel revenue events and map them through our conversion funnel. At each stage, calculate the drop-off rate and estimate the dollar impact of lost conversions. Identify the highest-impact stage to fix first. Update the revenue analysis in Google Sheets and post findings to #product in Slack",
		category: "Finance",
		integrations: [I.mixpanel, I["google-sheets"], I.slack],
	},
	{
		id: "amplitude-user-cohorts",
		title: "Why Do Users Drop Off After Week 1?",
		description:
			"Analyze week-1 retention cohorts to find what separates users who stay from those who churn",
		prompt:
			"Analyze Amplitude retention data for the past 3 months. Compare users who are active after week 1 vs those who churned — what features did retained users engage with? What's different about their onboarding path? Document the findings in Notion and post the key insight to #product in Slack",
		category: "Finance",
		integrations: [I.amplitude, I.notion, I.slack],
	},
	{
		id: "amazon-order-summary",
		title: "Order Status Summary",
		description: "List recent Amazon orders and summarize by status",
		prompt:
			"Pull our recent Amazon orders, group them by status (pending, shipped, delivered, returned), and summarize the totals",
		category: "Finance",
		integrations: [I.amazon],
	},
	{
		id: "snowflake-data-quality",
		title: "Is Any of Our Data Pipeline Broken?",
		description:
			"Detect tables with stale data, missing rows, or broken ETL jobs and alert the team",
		prompt:
			"Run data quality checks on our Snowflake warehouse. Flag any tables where the latest row is more than 24 hours old (stale ETL), row counts dropped more than 20% vs last week (broken pipeline), or null rates spiked on key columns. Post a pipeline health report to #data in Slack",
		category: "Finance",
		integrations: [I.snowflake, I.slack],
	},
	{
		id: "bigquery-usage-report",
		title: "Which BigQuery Queries Are Costing Us?",
		description:
			"Find the most expensive queries, suggest optimizations, and estimate monthly savings",
		prompt:
			"Review Google BigQuery query costs for this week. Identify the top 10 most expensive queries by bytes scanned. For each, suggest specific optimizations (partitioning, column selection, caching). Estimate potential monthly savings if the top 5 are optimized. Post the cost report to #data in Slack",
		category: "Finance",
		integrations: [I["google-bigquery"], I.slack],
	},
	{
		id: "bamboohr-time-off-summary",
		title: "Upcoming Time-Off & Gaps",
		description: "Pull upcoming time-off and flag coverage gaps",
		prompt:
			"Pull upcoming time-off requests from BambooHR for the next 2 weeks and flag any team coverage gaps",
		category: "Finance",
		integrations: [I.bamboohr],
	},
	{
		id: "stripe-churn-analysis",
		title: "Why Are Customers Cancelling?",
		description:
			"Analyze recent cancellations by reason, plan, and tenure to find patterns and recommend fixes",
		prompt:
			"Pull all Stripe subscription cancellations from the past 30 days. Break down by cancellation reason, plan type, and customer tenure. Identify the #1 pattern — is it onboarding, pricing, or missing features? Document the analysis in Notion with specific recommendations and post the summary to #product in Slack",
		category: "Finance",
		integrations: [I.stripe, I.notion, I.slack],
	},
	{
		id: "stripe-kpi-dashboard",
		title: "Update the Revenue Dashboard",
		description: "Pull key revenue metrics from Stripe and update the team's tracking spreadsheet",
		prompt:
			"Pull current MRR, ARR, net revenue retention, average revenue per user, and trial conversion rate from Stripe. Update each metric in our Google Sheets dashboard with this week's values and week-over-week change. Post a one-line summary to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"], I.slack],
	},
	{
		id: "shopify-inventory-risk",
		title: "Which Products Will Run Out of Stock?",
		description: "Forecast stockouts based on current sales velocity and inventory levels",
		prompt:
			"Pull Shopify inventory levels and sales velocity for all active products. Calculate days of stock remaining for each. Flag any product that will run out within 14 days at current pace. Update the inventory tracker in Google Sheets and post urgent stockout warnings to #ops in Slack",
		category: "Finance",
		integrations: [I.shopify, I["google-sheets"], I.slack],
	},
	{
		id: "stripe-monthly-financial-report",
		title: "Monthly Revenue & Subscription Report",
		description:
			"Pull detailed Stripe data — MRR, churn, expansion, contraction by plan — and build a board-ready report",
		prompt:
			"Pull Stripe subscription data for this month and build a detailed financial report: MRR, ARR, net revenue retention, gross churn, expansion revenue, contraction, and new business — all broken down by plan tier. Compare each metric to last month and highlight trends. Calculate unit economics: ARPU, LTV estimate, and payback period. Update the financial model in Google Sheets and post the executive summary to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"], I.slack],
	},
	{
		id: "invoice-chaser",
		title: "Chase Unpaid Invoices and Send Reminders",
		description:
			"Find all overdue invoices, draft polite payment reminder emails, and send them via Gmail",
		prompt:
			"Check Xero for invoices overdue by more than 7 days. For each overdue invoice, pull the client name, amount, due date, and days overdue. Draft a polite but firm payment reminder email customized for each — more urgent for 30+ days overdue, friendlier for 7-14 days. Send the reminders via Gmail and post a summary of outstanding receivables to #finance in Slack",
		category: "Finance",
		integrations: [I.xero, I.gmail, I.slack],
	},
	{
		id: "monthly-expense-review",
		title: "Review and Categorize This Month's Expenses",
		description:
			"Go through all expenses, categorize untagged ones, flag anomalies, and update the budget tracker",
		prompt:
			"Review this month's expenses in QuickBooks. Categorize any untagged transactions. Flag expenses that are unusual or significantly higher than last month's average for that category. Identify any subscriptions or recurring charges we might no longer need. Update the budget tracker in Google Sheets and post a summary to #finance in Slack with items that need attention",
		category: "Finance",
		integrations: [I.quickbooks, I["google-sheets"], I.slack],
	},
	{
		id: "brex-spend-optimizer",
		title: "Which Subscriptions and Expenses Can We Cut?",
		description:
			"Audit recurring charges, flag unused subscriptions, and estimate savings from cutting waste",
		prompt:
			"Pull this month's Brex transactions and identify all recurring charges and subscriptions. Flag any that haven't been used (no logins or activity) in 30+ days. Group expenses by category and compare to last month — flag any category that increased more than 25%. Calculate potential monthly savings from cutting unused subscriptions. Update the expense tracker in Google Sheets and post the savings opportunities to #finance in Slack",
		category: "Finance",
		integrations: [I.brex, I["google-sheets"], I.slack],
	},
	{
		id: "stripe-trial-conversion",
		title: "How Many Trials Are Converting to Paid?",
		description: "Track trial-to-paid conversion rates by cohort, plan, and signup source",
		prompt:
			"Pull Stripe trial data for the past 3 months. Calculate trial-to-paid conversion rate by week cohort, plan tier, and signup source (if available). Identify which cohorts convert best and worst. Check if trial length, plan pricing, or onboarding emails correlate with conversion. Update the conversion tracker in Google Sheets and post the trend analysis to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"], I.slack],
	},
	{
		id: "stripe-failed-payments",
		title: "Recover Failed Payments Before We Lose Them",
		description:
			"Find failed payment attempts, draft recovery emails, and estimate revenue at risk",
		prompt:
			"Pull all failed Stripe payment attempts from the past 7 days. For each, identify the customer, failure reason (card declined, insufficient funds, expired card), subscription plan, and MRR at risk. Draft a personalized recovery email for each — different tone for expired cards (simple update) vs declined (empathetic). Send via Gmail. Calculate total MRR at risk and post the recovery summary to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I.gmail, I.slack],
	},
	{
		id: "quickbooks-cash-flow-forecast",
		title: "Will We Run Out of Cash?",
		description:
			"Project cash runway based on current burn rate, incoming revenue, and outstanding invoices",
		prompt:
			"Pull QuickBooks data: current cash balance, monthly recurring expenses, accounts receivable, and accounts payable. Calculate our current monthly burn rate and project cash runway in weeks. Factor in expected incoming payments and upcoming expenses. Flag if runway drops below 6 months. Update the cash flow forecast in Google Sheets and post the runway status to #finance in Slack",
		category: "Finance",
		integrations: [I.quickbooks, I["google-sheets"], I.slack],
	},
	{
		id: "xero-monthly-pnl",
		title: "Build This Month's P&L Report",
		description:
			"Pull revenue and expenses from Xero and build a clean P&L with month-over-month comparison",
		prompt:
			"Pull this month's financial data from Xero and build a Profit & Loss report. Break down revenue by stream and expenses by category. Compare each line item to last month and flag any that changed more than 15%. Calculate gross margin, operating margin, and net margin. Update the P&L in Google Sheets and post the executive summary with key variances to #finance in Slack",
		category: "Finance",
		integrations: [I.xero, I["google-sheets"], I.slack],
	},
	{
		id: "shopify-product-profitability",
		title: "Which Products Actually Make Us Money?",
		description:
			"Calculate true profitability per product factoring in COGS, shipping, returns, and ad spend",
		prompt:
			"Pull Shopify sales data by product for the past month. For each product, calculate: revenue, units sold, return rate, and average order value. If COGS data is available, calculate gross margin per product. Identify the top 5 most profitable products and the bottom 5 money-losers. Recommend whether to promote winners more or discontinue losers. Update the product P&L in Google Sheets and post key findings to #finance in Slack",
		category: "Finance",
		integrations: [I.shopify, I["google-sheets"], I.slack],
	},
	{
		id: "stripe-pricing-analysis",
		title: "Are We Leaving Money on the Table with Pricing?",
		description: "Analyze plan distribution, upgrade patterns, and willingness-to-pay signals",
		prompt:
			"Pull Stripe subscription data and analyze our pricing: distribution of customers across plans, upgrade and downgrade patterns, average revenue per user by plan, and how long users stay on each tier before upgrading. Identify if most users cluster on the cheapest plan (price too low?), if upgrade rates are low (missing features in higher tiers?), or if churn concentrates on a specific plan. Document the pricing analysis in Notion with specific recommendations",
		category: "Finance",
		integrations: [I.stripe, I.notion],
	},
	{
		id: "amazon-seller-performance",
		title: "How Are Our Amazon Listings Performing?",
		description: "Track sales rank, buy box percentage, reviews, and returns by product",
		prompt:
			"Pull our Amazon seller data and analyze performance by listing: sales rank trend, buy box win percentage, review count and rating, return rate, and conversion rate. Flag any listing where sales rank dropped significantly, buy box share decreased, or returns spiked. Recommend specific actions for each problem listing — pricing adjustments, listing optimization, or review management. Update the tracker in Google Sheets and post alerts to #finance in Slack",
		category: "Finance",
		integrations: [I.amazon, I["google-sheets"], I.slack],
	},
	{
		id: "quickbooks-budget-vs-actuals",
		title: "Where Are We Overspending vs Budget?",
		description: "Pull actuals from QuickBooks, compare to budget in Sheets, and flag overspending",
		prompt:
			"Pull this month's actual expenses from QuickBooks by category. Compare every line item against our budget in Google Sheets. Flag any category where we're overspending by more than 10% or where spending is trending to exceed the monthly budget. Calculate total budget variance and forecast end-of-month position. Update the budget tracker in Sheets and post a summary of overspending alerts to #finance in Slack",
		category: "Finance",
		integrations: [I.quickbooks, I["google-sheets"], I.slack],
	},
	{
		id: "finance-forecast-vs-actuals",
		title: "How Accurate Was Our Financial Forecast?",
		description: "Compare forecast model with Stripe actuals and suggest forecasting improvements",
		prompt:
			"Pull actual revenue data from Stripe for the past month and compare it to our forecast model in Google Sheets. Calculate forecast accuracy by line item — where were we off by more than 10%? Identify systematic biases (consistently over- or under-forecasting specific revenue streams). Suggest specific adjustments to improve the forecasting model. Update the forecast-vs-actuals sheet and post the accuracy report to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"], I.slack],
	},
	{
		id: "vendor-cost-audit",
		title: "Which Vendor Services Are We Paying for but Not Using?",
		description:
			"Review all vendor charges, identify unused or underused services, and recommend cuts",
		prompt:
			"Pull all recurring vendor charges and subscriptions from Brex for the past 3 months. Identify services where spending increased unexpectedly, subscriptions that haven't been actively used (no logins or API calls), duplicate tools serving the same purpose, and contracts coming up for renewal. Calculate potential savings from cutting or downgrading each. Save the full vendor audit to Google Sheets and post the top cost-cutting recommendations to #finance in Slack",
		category: "Finance",
		integrations: [I.brex, I["google-sheets"], I.slack],
	},
	{
		id: "monthly-metrics-snapshot",
		title: "Pull This Month's Key Metrics and Flag Red Flags",
		description:
			"Pull KPIs from Stripe and GA, save to Sheets, and summarize highlights, lowlights, and red flags",
		prompt:
			"Pull this month's key metrics: MRR, new customers, churn rate, and net revenue retention from Stripe; traffic, signups, and conversion rates from Google Analytics. Save all metrics to our monthly reporting Google Sheet with month-over-month comparisons. Write an executive summary covering highlights (metrics that improved), lowlights (metrics that declined), and red flags (anything that needs immediate attention). Post the summary to #finance in Slack",
		category: "Finance",
		integrations: [I.stripe, I["google-analytics"], I["google-sheets"], I.slack],
	},
	{
		id: "customer-winback-campaign",
		title: "Which Churned Customers Should We Try to Win Back?",
		description: "Identify winnable churned customers and draft personalized re-engagement emails",
		prompt:
			"Pull churned Stripe customers from the last 60 days. Segment by churn reason (if available), previous plan tier, lifetime value, and how long they were customers. Identify the most winnable targets — high-LTV customers who churned recently and were previously active. Draft personalized win-back emails for the top 15 that acknowledge their departure, highlight what's new since they left, and offer a compelling reason to return. Save drafts to Gmail and post the win-back candidate list to #growth in Slack",
		category: "Finance",
		integrations: [I.stripe, I.gmail, I.slack],
	},
	{
		id: "referral-program-tracker",
		title: "How Is Our Referral Program Performing?",
		description:
			"Track referral-driven revenue, identify top referrers, and find ways to boost the program",
		prompt:
			"Pull Stripe data to analyze our referral program performance: total referral-driven revenue, number of referred customers, conversion rate from referral to paid, and average LTV of referred vs non-referred customers. Identify our top 10 referrers by revenue generated. Calculate program ROI including any referral rewards paid out. Save the full analysis to Google Sheets and post highlights to #growth in Slack with recommendations for boosting referral performance",
		category: "Finance",
		integrations: [I.stripe, I["google-sheets"], I.slack],
	},
	{
		id: "lever-recruiting-pipeline",
		title: "Recruiting Pipeline Summary",
		description: "Summarize candidates by stage, interviews, and offers pending",
		prompt:
			"Summarize our Lever recruiting pipeline — candidates by stage, interviews this week, and offers pending",
		category: "Finance",
		integrations: [I.lever],
	},
	// ── Remaining integrations ──
	{
		id: "figma-design-handoff",
		title: "Design Handoff Checklist",
		description: "Check for designs ready for dev handoff",
		prompt:
			"Review our Figma projects for designs marked as ready for development and create a handoff checklist",
		category: "Product",
		integrations: [I.figma],
	},
	{
		id: "adobe-project-status",
		title: "Creative Project Tracker",
		description: "List active Adobe projects and their completion status",
		prompt:
			"List our active Adobe projects and their status — flag any that are overdue or missing assignments",
		category: "Marketing",
		integrations: [I.adobe],
	},
	{
		id: "box-file-compliance",
		title: "File Compliance Audit",
		description: "Audit shared files for compliance and access issues",
		prompt:
			"Audit our Box folders for files with incorrect sharing permissions or missing compliance tags",
		category: "Operations",
		integrations: [I.box],
	},
	{
		id: "dropbox-shared-links-audit",
		title: "Audit Shared Links",
		description: "Review shared links and flag expired or public ones",
		prompt:
			"Review our Dropbox shared links and flag any that are expired, publicly accessible, or no longer needed",
		category: "Operations",
		integrations: [I.dropbox],
	},
	{
		id: "heygen-video-status",
		title: "Video Generation Tracker",
		description: "Check video generation jobs and flag failures",
		prompt:
			"Check our HeyGen video generation queue and summarize completed, pending, and failed jobs",
		category: "Marketing",
		integrations: [I.heygen],
	},
	{
		id: "supabase-table-review",
		title: "Database Health Check",
		description: "List tables and check for empty or low-record tables",
		prompt:
			"List all tables in our Supabase project, check row counts, and flag any tables that look empty or have suspiciously low data",
		category: "Product",
		integrations: [I.supabase],
	},
	{
		id: "calcom-booking-summary",
		title: "Upcoming Bookings & Conflicts",
		description: "Summarize upcoming bookings and availability gaps",
		prompt:
			"Summarize my Cal.com bookings for the coming week and flag any scheduling conflicts or availability gaps",
		category: "Operations",
		integrations: [I.calcom],
	},
	{
		id: "contentful-content-review",
		title: "Catch Stale CMS Content",
		description: "Review CMS entries for outdated or draft content",
		prompt:
			"Review our Contentful CMS for entries stuck in draft, outdated content, or missing translations",
		category: "Marketing",
		integrations: [I.contentful],
	},
	{
		id: "digitalocean-infra-check",
		title: "Are We Overpaying for Server Resources?",
		description:
			"Check server utilization, flag underused resources, and estimate savings from downsizing",
		prompt:
			"List our DigitalOcean droplets and databases. Check CPU and memory utilization for each over the past 7 days. Flag any resources consistently under 20% utilization — these are candidates for downsizing. Estimate monthly savings from right-sizing and post recommendations to #engineering in Slack",
		category: "Product",
		integrations: [I.digitalocean, I.slack],
	},
	{
		id: "azure-resource-audit",
		title: "Find Unused Cloud Resources",
		description: "List Azure resources and flag unused or misconfigured ones",
		prompt:
			"List our Azure resources across subscriptions, flag any that look unused or misconfigured, and summarize monthly spend",
		category: "Product",
		integrations: [I.azure],
	},
	{
		id: "gcloud-project-review",
		title: "Cloud Cost & Config Review",
		description: "Review GCP projects and flag cost or config issues",
		prompt:
			"Review our Google Cloud projects, list active services, and flag anything with unusual cost increases or misconfigurations",
		category: "Product",
		integrations: [I.gcloud],
	},
	{
		id: "aws-cloudwatch-alert-review",
		title: "What Broke in Production Last Night?",
		description:
			"Review overnight errors, identify root causes, and create tickets for anything new",
		prompt:
			"Search our AWS CloudWatch logs for errors and exceptions from the last 12 hours. Group by service and error type. Identify root causes for the top 5 issues. Create Linear tickets for any new errors not yet tracked and post a morning production health report to #engineering in Slack",
		category: "Product",
		integrations: [I["aws-cloudwatch"], I.linear, I.slack],
	},
	{
		id: "aws-knowledge-docs-lookup",
		title: "Look Up AWS Best Practices",
		description: "Search AWS documentation for service guidance",
		prompt:
			"Search AWS documentation for best practices on setting up a multi-region failover architecture and summarize the key steps",
		category: "Product",
		integrations: [I["aws-knowledge"]],
	},
	{
		id: "gcloud-cost-anomaly",
		title: "Did Any Cloud Costs Spike This Week?",
		description:
			"Compare this week's GCP costs to last week and flag any services with unusual increases",
		prompt:
			"Pull Google Cloud billing data for this week and compare to last week by service. Flag any service where costs increased more than 20%. Investigate the likely cause (traffic spike, misconfiguration, new resource) and post the cost anomaly report to #engineering in Slack",
		category: "Product",
		integrations: [I.gcloud, I.slack],
	},
	// ── Search, Scraping & AI Tools ──
	{
		id: "tavily-market-research",
		title: "Competitive Intelligence Scan",
		description: "Search the web for competitive intelligence",
		prompt:
			"Use Tavily to search for recent news, reviews, and pricing changes from our top 3 competitors and compile a summary",
		category: "Marketing",
		integrations: [I.tavily],
	},
	{
		id: "firecrawl-content-audit",
		title: "Audit Site Meta & Titles",
		description: "Crawl our site and flag content issues",
		prompt:
			"Crawl our marketing site with Firecrawl, extract all page titles and meta descriptions, and flag any that are missing or duplicated",
		category: "Marketing",
		integrations: [I.firecrawl],
	},
	{
		id: "nia-codebase-analysis",
		title: "Codebase Quality Scan",
		description: "Search indexed repos for patterns and issues",
		prompt:
			"Search our indexed codebase in Nia for TODO comments, deprecated API usage, and known anti-patterns, then summarize the findings",
		category: "Product",
		integrations: [I.nia],
	},
	{
		id: "zapier-workflow-audit",
		title: "Audit Broken Automations",
		description: "Review Zapier automations for errors and usage",
		prompt:
			"List our active Zapier workflows, check for any that have errored recently, and flag automations that haven't run in 30+ days",
		category: "Operations",
		integrations: [I.zapier],
	},
	{
		id: "adrapid-banner-generation",
		title: "Generate Ad Banners",
		description: "Generate ad banners for a campaign",
		prompt:
			"Generate display ad banners for our upcoming product launch campaign using Adrapid — standard sizes for Google Display Network",
		category: "Marketing",
		integrations: [I.adrapid],
	},
];

/**
 * Number of templates to display per page
 */
export const TEMPLATES_PER_PAGE = 6;

/**
 * All integrations referenced by templates, deduplicated and sorted by name.
 * Derived automatically so it stays in sync with TEMPLATES.
 */
export const TEMPLATE_INTEGRATIONS: IntegrationRef[] = (() => {
	const seen = new Set<string>();
	const refs: IntegrationRef[] = [];
	for (const t of TEMPLATES) {
		for (const i of t.integrations) {
			if (!seen.has(i.id)) {
				seen.add(i.id);
				refs.push(i);
			}
		}
	}
	return refs.sort((a, b) => a.name.localeCompare(b.name));
})();
